import { useEffect, useRef, useState, useCallback, Activity, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Server, Phone, Mail, AlertTriangle, Tag, Globe } from "lucide-react";
import AxiosDB from "@/Config/AxiosDB";


const POLL_MS = 20000;          // frecuencia de sondeo del servidor
const IDLE_MS = 5 * 60 * 1000;  // 5 min sin actividad => IDLE
const MAX_REGISTROS = 3600;     // máx 60 min a razón de 1 registro/seg
const VENTANA_MS = 60 * 60 * 1000; // 60 min

const CONFIG = {
  ONLINE: { label: "ONLINE", dot: "bg-green-500", text: "text-green-700", ring: "ring-green-200", bar: "bg-green-500" },
  IDLE: { label: "IDLE", dot: "bg-amber-500", text: "text-amber-700", ring: "ring-amber-200", bar: "bg-amber-400" },
  OFFLINE: { label: "OFFLINE", dot: "bg-red-500", text: "text-red-700", ring: "ring-red-200", bar: "bg-red-500" },
  default: { label: "stating", dot: "bg-amber-500", text: "text-red-700", ring: "ring-red-200", bar: "bg-amber-400" },
};

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const fmtHora = (t) => {
  try { return new Date(t).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};



export default function ServerStatus() {
  const [status, setStatus] = useState("ONLINE");
  const [version, setVersion] = useState("");
  const [ip, setIP] = useState("");
  const [lastConnection, setLastConnection] = useState(localStorage.getItem("server_last_connection") || "");
  const [popupOpen, setPopupOpen] = useState(false);
  const [offlineDismissed, setOfflineDismissed] = useState(false);
  const lastActivity = useRef(Date.now());
  const [historial, setHistorial] = useState([]);
  const statusRef = useRef(status);

  useEffect(() => { statusRef.current = status; }, [status]);

  const ping = useCallback(async () => {
    try {
      await AxiosDB.get(`/server-status`, { timeout: 8000 })
        .then(({ data }) => {
          setLastConnection(data.server_time);

          setStatus(data.status);
          setVersion(data.version)
          setIP(data.ip)
          // localStorage.setItem("server_last_connection", data.server_time);
          console.log('historial', data)

          if (Array.isArray(data?.historial)) setHistorial(data.historial);

          console.log('historial', historial)

        }).catch((e) => {
          console.log(e)
        })

    } catch (e) {
      setStatus("OFFLINE");
    }
  }, []);

  useEffect(() => {
    ping();
    const interval = setInterval(ping, POLL_MS);
    const onActivity = () => { lastActivity.current = Date.now(); };
    ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true })
    );
    return () => {
      clearInterval(interval);
      ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((ev) =>
        window.removeEventListener(ev, onActivity)
      );
    };
  }, [ping]);



  // Apertura automática del popup cuando el servidor está OFFLINE
  useEffect(() => {
    if (status !== "OFFLINE") {
      setOfflineDismissed(false);
      return;
    }
    if (!offlineDismissed) {
      if (version) {
        setPopupOpen(true);
      }
    }
  }, [status]);  // eslint-disable-line

  const cfg = CONFIG[status] ?? CONFIG.default;

  const handleOpenChange = (open) => {
    setPopupOpen(open);
    // Al cerrar estando OFFLINE, no volver a abrir automáticamente hasta refrescar o hacer click
    if (!open && status === "OFFLINE") {
      setOfflineDismissed(true);
    }
  };


  // Segmentos (agrupando estados consecutivos) y porcentajes
  const { segmentos, porcentajes, total } = useMemo(() => {
    const segs = [];
    const cnt = { online: 0, idle: 0, offline: 0 };
    historial.forEach((r) => {
      cnt[r.s.toLowerCase()] = (cnt[r.s.toLowerCase()] || 0) + 1;
      const last = segs[segs.length - 1];
      if (last && last.s === r.s) last.count += 1;
      else segs.push({ s: r.s, count: 1 });
    });
    const tot = historial.length || 1;
    const pct = {
      online: ((cnt['online'] / tot) * 100).toFixed(1),
      idle: ((cnt['idle'] / tot) * 100).toFixed(1),
      offline: ((cnt['offline'] / tot) * 100).toFixed(1),
    };
    return { segmentos: segs, porcentajes: pct, total: historial.length };
  }, [historial]);

  console.log('porcentajes', porcentajes)
  return (
    <>
      <button
        onClick={() => setPopupOpen(true)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors ring-1 ${cfg.ring}`}
        data-testid="server-status-indicator"
        title="Estado del servidor"
      >
        <Server className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-600">Status Server:</span>
        <span className="flex items-center gap-1.5 ml-auto">
          <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${status !== "OFFLINE" ? "animate-pulse" : ""}`} />
          <span className={`text-xs font-semibold ${cfg.text}`} data-testid="server-status-label">{cfg.label}</span>
        </span>
      </button>
      {status === "OFFLINE" && (
        <p className="text-[11px] text-slate-500 mt-1 px-1" data-testid="server-last-connection">
          Última conexión: {fmtFecha(lastConnection)}
        </p>
      )}

      <Dialog open={popupOpen} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="server-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${cfg.dot}`} />
              Estado su Servidor: {cfg.label}
            </DialogTitle>
            <DialogDescription>
              {status === "OFFLINE"
                ? "No se pudo establecer conexión con su servidor, favor verifique su conexion a internet."
                : status === "idle"
                  ? "El servidor está disponible pero la sesión se encuentra inactiva."
                  : "El servidor está en línea y funcionando correctamente."}
            </DialogDescription>
          </DialogHeader>

          {status === "OFFLINE" && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 space-y-3" data-testid="server-offline-info">
              <div className="flex items-start gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="text-sm">
                  Por favor, <strong>contacte a su soporte técnico</strong> para la revisión de su servidor.
                </p>
              </div>
              <div className="text-sm text-slate-700 space-y-1">
                <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-500" /> Soporte: (849) 937-0818</p>
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500" /> soporte@dasoft.com.do</p>
                <p className="text-xs text-slate-500">Última conexión: {fmtFecha(lastConnection)}</p>
              </div>
            </div>
          )}

          {status !== "OFFLINE" && (
            <div className="text-sm text-slate-700 space-y-1">
              <p>Si experimenta algún inconveniente, contacte a su soporte técnico para la revisión de su servidor.</p>
              <p className="flex items-center gap-2 mt-2"><Phone className="w-4 h-4 text-slate-500" /> Soporte: (849) 937-0818 </p>
              <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500" /> soporte@dasoft.com.do</p>
            </div>
          )}


          {/* Gráfico de disponibilidad */}
          <div className="mt-3 pt-3 border-t border-slate-200" data-testid="server-disponibilidad">
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2">
              <Activity className="w-3.5 h-3.5" /> Disponibilidad (últimos 60 min · {total} registros)
            </p>

            {total === 0 ? (
              <p className="text-xs text-slate-400 py-3 text-center">Recopilando datos de disponibilidad...</p>
            ) : (
              <>
                <div className="flex h-6 w-full rounded overflow-hidden border border-slate-200" data-testid="disponibilidad-barra">
                  {segmentos.map((seg, i) => (
                    <div
                      key={i}
                      style={{ width: `${(seg.count / total) * 100}%` }}
                      className={CONFIG[seg.s]?.bar || ''}
                      title={`${CONFIG[seg.s]?.label || ''} · ${seg.count}s`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>{historial.length ? fmtHora(historial[0].t) : ""}</span>
                  <span>ahora</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div className="flex items-center gap-1.5" data-testid="disp-online">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Online <span className="ml-auto font-semibold">{porcentajes.online}%</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="disp-idle">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Idle <span className="ml-auto font-semibold">{porcentajes.idle}%</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="disp-offline">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Offline <span className="ml-auto font-semibold">{porcentajes.offline}%</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-2 pt-3 border-t border-slate-200 text-xs text-slate-500 space-y-1" data-testid="server-tech-info">
            <p className="flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> Versión del servicio: <span className="font-mono text-slate-700" data-testid="server-version">{version || "—"}</span></p>
            <p className="flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> IP del Servidor Host: <span className="font-mono text-slate-700" data-testid="server-ip">{ip || "—"}</span></p>
          </div>

          <DialogFooter>
            <Button onClick={() => handleOpenChange(false)} data-testid="server-status-close">Entendido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
